/**
 * The guest wall's state, as the Go API stores and serves it.
 *
 * The API models this as an opaque blob — `GinActivityStateResponse.data` is
 * `Record<string, unknown>` — because `ActivityState` is a generic per-activity
 * document. So the shape lives here, on the reading side, along with the
 * defaults merge that makes a partially-populated document safe to render.
 *
 * These types deliberately duplicate the legacy `lib/api/client/types.gen.ts`
 * shapes rather than importing them: the point of the migration is that the
 * wall no longer depends on that module.
 */

export type WallDisplayMethod = 'bubbles' | 'wheel' | 'slots'

export type WallSettings = {
  spinDuration: number
  excludeWinners: boolean
  autoClose: boolean
  autoCloseDelay: number
  displayMethod: WallDisplayMethod
  maritalStatusFilter: string[]
  backgroundImagePath?: string
  backgroundImageUrl?: string
}

export type WallWinner = {
  id: string
  name: string
  pictureUrl?: string
  timestamp: number
  sessionId?: string
} | null

export type WallSession = {
  id: string
  name: string
  createdAt: number
  settings: WallSettings
}

export type WallState = {
  isSpinning: boolean
  winner: WallWinner
  winners: NonNullable<WallWinner>[]
  currentSessionId: string
  sessions: WallSession[]
  settings: WallSettings
  /**
   * When the current winner was first shown, stamped server-side on write
   * (activity.Service.UpdateWallState). Not part of the legacy shape, but the
   * display needs it to schedule a re-read at the auto-close deadline: the
   * server clears an expired winner on read (docs/adr/0003) and leaves this
   * timestamp in place, so it survives even when `winner` reads as null.
   */
  winnerShownAt?: number
}

export const DEFAULT_WALL_SETTINGS: WallSettings = {
  spinDuration: 5,
  excludeWinners: true,
  autoClose: true,
  autoCloseDelay: 10,
  displayMethod: 'bubbles',
  maritalStatusFilter: [],
  backgroundImagePath: '',
  backgroundImageUrl: '',
}

export const DEFAULT_WALL_STATE: WallState = {
  isSpinning: false,
  winner: null,
  winners: [],
  currentSessionId: 'default',
  sessions: [
    {
      id: 'default',
      name: 'เซสชันที่ 1',
      createdAt: Date.now(),
      settings: DEFAULT_WALL_SETTINGS,
    },
  ],
  settings: DEFAULT_WALL_SETTINGS,
}

/** Mongo and the old RTDB both round-trip arrays as keyed objects. */
function toArray<T>(value: unknown): T[] | null {
  if (Array.isArray(value)) return value as T[]
  if (value && typeof value === 'object') return Object.values(value) as T[]
  return null
}

/**
 * Merges a raw wall blob over the defaults.
 *
 * The Go side seeds an identical default document on first read
 * (activity.Service.defaultWallState), but a document written before a field
 * existed is never backfilled — so this merge stays load-bearing rather than
 * being belt-and-braces. It is also what guarantees callers see a defined
 * `settings.backgroundImageUrl` instead of `undefined`.
 */
export function normalizeWallState(
  data: Record<string, unknown> | undefined,
): WallState {
  if (!data) return DEFAULT_WALL_STATE

  const settings: WallSettings = {
    ...DEFAULT_WALL_SETTINGS,
    ...(data.settings as Partial<WallSettings> | undefined),
  }

  const sessions =
    toArray<WallSession>(data.sessions)?.map((s) => ({
      ...s,
      settings: { ...DEFAULT_WALL_SETTINGS, ...s.settings },
    })) ?? DEFAULT_WALL_STATE.sessions

  return {
    ...DEFAULT_WALL_STATE,
    ...(data as Partial<WallState>),
    winners: toArray<NonNullable<WallWinner>>(data.winners) ?? [],
    sessions,
    settings,
  }
}
